/**
 * Utility functions for manipulating strings in various naming conventions.
 *
 * The core of the implementation relies on the {@link WORD_SPLIT_REGEX}
 * which splits a string into “words” according to camelCase / PascalCase,
 * numeric tokens and sequences of capital letters (e.g. `XMLHttpRequest`).
 *
 * @example
 * ```ts
 * StringUtils.toSnakeCase('fooBar'); // 'foo_bar'
 * StringUtils.toCamelCase('Foo_Bar'); // 'fooBar'
 * ```
 */
export class StringUtils {
    /**
     * Regular expression used by {@link splitIntoWords} to isolate words
     * within camelCase / PascalCase strings.
     *
     * The pattern works for:
     * - `fooBar`
     * - `FooBar`
     * - `fooBAR`
     * - `foo123Bar`
     * - `XMLHttpRequest`
     *
     * It captures:
     * 1. sequences of capital letters that precede a capital–lowercase pair
     *    (e.g. `XML` in `XMLHttpRequest`);
     * 2. an optional leading capital followed by one or more lowercase letters
     *    (e.g. `Http`);
     * 3. sequences of digits.
     */
    private static readonly WORD_SPLIT_REGEX = /[A-Z]+(?=[A-Z][a-z])|[A-Z]?[a-z]+|[0-9]+|[A-Z]+/g;

    // =======================================================================
    //  Public API
    // =======================================================================

    /**
     * Convert a string into snake_case.
     *
     * @param str The string to transform.
     * @returns The snake‑cased representation.
     *
     * @example
     * ```ts
     * StringUtils.toSnakeCase('fooBar'); // 'foo_bar'
     * ```
     */
    public static toSnakeCase(str: string): string {
        return StringUtils.splitIntoWords(str).join('_').toLowerCase();
    }

    /**
     * Convert a string into kebab-case.
     *
     * @param str The string to transform.
     * @returns The kebab‑cased representation.
     *
     * @example
     * ```ts
     * StringUtils.toKebabCase('fooBar'); // 'foo-bar'
     * ```
     */
    public static toKebabCase(str: string): string {
        return StringUtils.splitIntoWords(str).join('-').toLowerCase();
    }

    /**
     * Convert a string into camelCase.
     *
     * @param str The string to transform.
     * @returns The camel‑cased representation.
     *
     * @example
     * ```ts
     * StringUtils.toCamelCase('Foo_Bar'); // 'fooBar'
     * StringUtils.toCamelCase('foo-bar'); // 'fooBar'
     * ```
     */
    public static toCamelCase(str: string): string {
        const words = StringUtils.splitIntoWords(str);
        if (words.length === 0) return '';
        return words[0].toLowerCase() + words.slice(1).map(StringUtils.capitalize).join('');
    }

    /**
     * Convert a string into PascalCase.
     *
     * @param str The string to transform.
     * @returns The Pascal‑cased representation.
     *
     * @example
     * ```ts
     * StringUtils.toPascalCase('foo_bar'); // 'FooBar'
     * StringUtils.toPascalCase('foo-bar'); // 'FooBar'
     * ```
     */
    public static toPascalCase(str: string): string {
        return StringUtils.splitIntoWords(str).map(StringUtils.capitalize).join('');
    }

    /**
     * Convert a string into Title Case (each word capitalised and separated by a space).
     *
     * @param str The string to transform.
     * @returns The title‑cased representation.
     *
     * @example
     * ```ts
     * StringUtils.toTitleCase('foo_bar'); // 'Foo Bar'
     * StringUtils.toTitleCase('fooBar');   // 'Foo Bar'
     * ```
     */
    public static toTitleCase(str: string): string {
        return StringUtils.splitIntoWords(str).map(StringUtils.capitalize).join(' ');
    }

    /**
     * Convert a string into Sentence case (first word capitalised, the rest lower‑case).
     *
     * @param str The string to transform.
     * @returns The sentence‑cased representation.
     *
     * @example
     * ```ts
     * StringUtils.toSentenceCase('foo bar');    // 'Foo bar'
     * StringUtils.toSentenceCase('FOO_BAR');    // 'Foo bar'
     * ```
     */
    public static toSentenceCase(str: string): string {
        const words = StringUtils.splitIntoWords(str);
        if (words.length === 0) return '';
        const first = StringUtils.capitalize(words[0]);
        const rest = words
            .slice(1)
            .map((w) => w.toLowerCase())
            .join(' ');
        return [first, rest].filter(Boolean).join(' ');
    }

    /**
     * Convert a string into a single space‑separated list of lower‑case words.
     *
     * @param str The string to transform.
     * @returns The lower‑cased representation.
     *
     * @example
     * ```ts
     * StringUtils.toLowerCase('Foo_Bar'); // 'foo bar'
     * ```
     */
    public static toLowerCase(str: string): string {
        return StringUtils.splitIntoWords(str).join(' ').toLowerCase();
    }

    /**
     * Convert a string into a single space‑separated list of upper‑case words.
     *
     * @param str The string to transform.
     * @returns The upper‑cased representation.
     *
     * @example
     * ```ts
     * StringUtils.toUpperCase('foo_bar'); // 'FOO BAR'
     * ```
     */
    public static toUpperCase(str: string): string {
        return StringUtils.splitIntoWords(str).join(' ').toUpperCase();
    }

    // ===============================================================
    // ===============================================================

    /**
     * Transform an input string like 'USER_ROLE' into 'User Role'
     * @param input Input string
     * @returns Transformed string
     */
    public static toHumanFormatedStr(input: string): string {
        let output = '';
        let isUpper = input.charAt(0) == input.charAt(0).toUpperCase();
        let isNewWord = true;

        for (let i = 0; i < input.length; ++i) {
            if (input.charAt(i) == '_' || input.charAt(i) == ' ') {
                output += ' ';
                isNewWord = true;
                continue;
            }

            const currentIsUpper = input.charAt(i) == input.charAt(i).toUpperCase();
            if (currentIsUpper && !isUpper) {
                output += ' ';
                isNewWord = true;
            }
            isUpper = currentIsUpper;

            if (isNewWord) output += input.charAt(i).toUpperCase();
            else output += input.charAt(i).toLowerCase();

            isNewWord = false;
        }

        return output;
    }

    // ===============================================================
    // ===============================================================

    /**
     * Split an input string into an array of words.
     *
     * The method first normalises separators (`_`, `-`, and multiple spaces)
     * into a single space.  It then splits on spaces and applies
     * {@link WORD_SPLIT_REGEX} to each segment to extract individual words.
     *
     * @param str The string to split.
     * @returns An array of words.  Empty strings return an empty array.
     */
    private static splitIntoWords(str: string): string[] {
        if (!str) return [];
        const cleaned = str.replace(/[ _-]+/g, ' ');
        const words: string[] = [];
        for (const part of cleaned.split(/\s+/)) {
            const sub = part.match(StringUtils.WORD_SPLIT_REGEX);
            if (sub) words.push(...sub);
        }
        return words;
    }

    /**
     * Capitalise a single word.
     *
     * The first character is upper‑cased and the rest are lower‑cased.
     *
     * @param word The word to capitalise.
     * @returns The capitalised word, or an empty string if `word` is falsy.
     */
    private static capitalize(word: string): string {
        if (!word) return '';
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    }
}
